import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { adminListContractOrders, adminUpdateContractOrder } from "@/lib/api/contract-orders.functions";
import { adminResendPayfastReceipt } from "@/lib/api/payments-admin.functions";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/contract-orders")({
  component: AdminContractOrders,
});

function AdminContractOrders() {
  const fetchList = useServerFn(adminListContractOrders);
  const update = useServerFn(adminUpdateContractOrder);
  const sendInvoice = useServerFn(adminResendPayfastReceipt);
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ["admin-contract-orders"], queryFn: () => fetchList() });

  const mut = useMutation({
    mutationFn: (vars: { id: string; status: "pending" | "paid" | "cancelled" }) =>
      update({ data: vars }),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["admin-contract-orders"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  async function handleSendConfirmedInvoice(reference: string) {
    setBusy(reference);
    try {
      const r: any = await sendInvoice({ data: { reference } });
      if (r?.ok) toast.success(`Confirmed invoice emailed to ${r.email}`);
      else if (r?.queued) toast.info("Email queued for retry — we'll keep trying.");
      else toast.error(r?.reason || "Could not send invoice");
      qc.invalidateQueries({ queryKey: ["admin-contract-orders"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusy(null);
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const orders = data?.orders ?? [];

  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl text-navy">Contract orders</h2>
      <p className="text-xs text-muted-foreground">
        PayFast payments are confirmed automatically by webhook. Once an order is paid,
        click <strong>Send confirmed invoice</strong> to email the customer the branded
        tax invoice / receipt. The buyer is emailed the unlock password automatically.
      </p>
      {orders.length === 0 ? (
        <p className="text-sm text-muted-foreground">No orders yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Invoice sent</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {orders.map((o: any) => {
                const isPaid = o.status === "paid";
                const sent = !!o.receipt_sent_at;
                const isBusy = busy === o.reference;
                return (
                  <tr key={o.id}>
                    <td className="px-3 py-2 font-mono text-xs">{o.reference}</td>
                    <td className="px-3 py-2">{o.title}</td>
                    <td className="px-3 py-2 text-xs">{o.client_name}<br /><span className="text-muted-foreground">{o.client_email}</span></td>
                    <td className="px-3 py-2 text-right">R{Number(o.amount_zar).toFixed(2)}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${isPaid ? "bg-emerald-100 text-emerald-800" : o.status === "cancelled" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{o.status}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {sent ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {new Date(o.receipt_sent_at).toLocaleString("en-ZA")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not sent</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{new Date(o.created_at).toLocaleString("en-ZA")}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {!isPaid && (
                          <Button size="sm" onClick={() => mut.mutate({ id: o.id, status: "paid" })} className="bg-emerald-600 hover:bg-emerald-700">Mark paid</Button>
                        )}
                        {o.status === "pending" && (
                          <Button size="sm" variant="outline" onClick={() => mut.mutate({ id: o.id, status: "cancelled" })}>Cancel</Button>
                        )}
                        {isPaid && (
                          <Button
                            size="sm"
                            variant={sent ? "outline" : "default"}
                            disabled={isBusy}
                            onClick={() => handleSendConfirmedInvoice(o.reference)}
                          >
                            {isBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1 h-3.5 w-3.5" />}
                            {sent ? "Re-send invoice" : "Send confirmed invoice"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
